/**
 * One-time pop-up after a project is split into structure groups. Shown to each
 * user the first time they land on the Files tab after the split (see
 * `projectFileSplitAnnouncement.ts`); "Got it" dismisses it for good. The split
 * diagram itself lives in the hover card on the Deliverables rail title.
 */
import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { GitFork } from 'lucide-react';
import { useCRM } from '../../utils/CRMContext';
import type { SplitHistoryEvent } from '../../utils/projectFileSplitHistory';
import { fmtSplitWhen } from './SplitHistoryPopover';
import './SplitHistoryPopover.css';

export interface SplitAnnouncementProps {
  event: SplitHistoryEvent;
  /** Signed-in user; used to say "you" when they ran the split. */
  currentUid?: string | null;
  onAcknowledge: () => void;
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

const SplitAnnouncement: React.FC<SplitAnnouncementProps> = ({ event, currentUid, onAcknowledge }) => {
  const { crmUsers } = useCRM();
  const ackRef = useRef<HTMLButtonElement>(null);
  const who =
    event.byUid && currentUid && event.byUid === currentUid
      ? 'You'
      : (event.byUid && crmUsers.find(u => u.uid === event.byUid)?.displayName) || 'A teammate';

  useEffect(() => {
    ackRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onAcknowledge();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onAcknowledge]);

  return createPortal(
    <div className="pf-modal-overlay" role="presentation">
      <div
        className="pf-modal pf-split-announce"
        role="dialog"
        aria-modal="true"
        aria-labelledby="pf-split-announce-title"
        data-tour="pf-split-announce"
      >
        <span className="pf-split-announce-icon" aria-hidden>
          <GitFork size={22} />
        </span>
        <h3 className="pf-modal-title" id="pf-split-announce-title">
          This project&apos;s files are now split by structure groups
        </h3>
        <p className="pf-split-announce-lead">
          {who} split {plural(event.roots.length, 'whole-project track', 'whole-project tracks')} into{' '}
          {plural(event.groups.length, 'structure group', 'structure groups')} on {fmtSplitWhen(event.at)}:
        </p>
        <ul className="pf-split-announce-groups">
          {event.groups.map(g => {
            const multi = g.structureNames.length > 1;
            return (
              <li key={g.key}>
                <div className="pf-split-announce-group-row">
                  <b>{g.label}</b>
                  <span className="pf-split-announce-group-count">{plural(g.streams.length, 'track', 'tracks')}</span>
                </div>
                {multi ? <div className="pf-split-announce-group-meta">{g.structureNames.join(' · ')}</div> : null}
              </li>
            );
          })}
        </ul>
        <p className="pf-split-announce-points">
          Tracks now sit under these headings in the Deliverables rail. Every group inherits the earlier versions
          (nothing was copied; numbering carries on), new uploads go on the group tracks, and the whole-project roots
          are read-only and no longer listed.
        </p>
        <p className="pf-split-announce-hint">
          Hover the <GitFork size={11} aria-hidden /> badge beside “Deliverables” any time to see how the split maps out.
        </p>
        <div className="pf-split-announce-actions">
          <button ref={ackRef} type="button" className="pf-split-announce-ack" onClick={onAcknowledge}>
            Got it
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
};

export default SplitAnnouncement;
